import React, { Suspense } from 'react';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { StaleSharedSingletonBoundary } from '@honeybook/mfe-loader-web';
import RemoteComponentLoader from '~/RemoteComponentLoader';
import RouterLoader from '~/RouterLoader';
import apps from '../apps.json';
import { RemoteAppData } from '~/types';
import { getCurrentAppScope } from '~/router.utils';
import { queryClient } from './QueryClient';

const CXShell = React.lazy(() => import('./cx/CXShell'));

const remoteApps = apps as RemoteAppData[];
const currentAppScope = getCurrentAppScope();
// Apps the router renders directly: match the current scope, and exclude
// host-rendered apps (a `.hosted` scope means a host — the CX shell, or MX for
// its federated module remotes like tasks/layout — renders those instead, so
// they carry no route path here).
const scopedRemoteApps = remoteApps.filter(
    app =>
        !app.scopes.some(scope => scope.includes('.hosted')) &&
        app.scopes.includes(currentAppScope)
);

// CX-shell-hosted apps (both auth branches). The CX shell owns their paths and
// renders them under its provider stack.
const hostedCxApps = remoteApps.filter(
    app =>
        !!app.path &&
        app.scopes.some(
            scope => scope.startsWith('cx') && scope.includes('.hosted')
        )
);
const cxShellPaths = hostedCxApps.map(app => app.path);

const defaultFallbackApp = scopedRemoteApps.find(
    app => app.defaultFallbackRoute
);

const routedApps = scopedRemoteApps.filter(app => !app.defaultFallbackRoute);

const renderRemoteAppRoute = (appData: RemoteAppData) => (
    <Route
        key={appData.name}
        path={appData.path}
        render={() => <RemoteComponentLoader appData={appData} />}
    />
);

export function App() {
    return (
        <StaleSharedSingletonBoundary>
            <BrowserRouter>
                <QueryClientProvider client={queryClient}>
                    <Switch>
                        {routedApps.map(renderRemoteAppRoute)}
                        {currentAppScope === 'cx' &&
                            hostedCxApps.length > 0 && (
                                <Route path={cxShellPaths}>
                                    <Suspense fallback={<RouterLoader />}>
                                        <CXShell />
                                    </Suspense>
                                </Route>
                            )}
                        {defaultFallbackApp &&
                            renderRemoteAppRoute(defaultFallbackApp)}
                        {defaultFallbackApp && (
                            <Redirect to={defaultFallbackApp.path} />
                        )}
                    </Switch>
                </QueryClientProvider>
            </BrowserRouter>
        </StaleSharedSingletonBoundary>
    );
}
